import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { z } from 'zod';
import { login } from '@/api/auth';
import { isApiError } from '@/api/errors';
import { setSession, useSession } from '@/auth/session';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { PageSpinner } from '@/ui/Spinner';

const loginSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Enter your email address.')
    .email('Enter a valid email address.'),
  password: z.string().min(1, 'Enter your password.'),
});
type LoginForm = z.infer<typeof loginSchema>;

export function LoginPage() {
  const { session, isPending } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const destination = safeDestination(location.state);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const signIn = useMutation({
    mutationFn: login,
    onSuccess: (signedIn) => {
      setSession(queryClient, signedIn);
      void navigate(destination, { replace: true });
    },
  });

  if (isPending) return <PageSpinner label="Checking your session" />;
  if (session && !signIn.isPending) return <Navigate to={destination} replace />;

  return (
    <div className="flex min-h-svh items-center justify-center bg-muted p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="items-center text-center">
          <span className="text-sm font-semibold">WaSparks Social</span>
          <span className="text-xs text-muted-foreground">Platform admin</span>
          <h1 className="pt-2 text-lg font-medium">Sign in</h1>
        </CardHeader>

        <CardContent>
          {signIn.isError && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{loginErrorMessage(signIn.error)}</AlertDescription>
            </Alert>
          )}

          <form noValidate onSubmit={handleSubmit((values) => signIn.mutate(values))}>
            <FieldGroup>
              <Field data-invalid={Boolean(errors.email)}>
                <FieldLabel htmlFor="email">Email</FieldLabel>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  autoFocus
                  aria-invalid={Boolean(errors.email)}
                  {...register('email')}
                />
                <FieldError errors={[errors.email]} />
              </Field>
              <Field data-invalid={Boolean(errors.password)}>
                <FieldLabel htmlFor="password">Password</FieldLabel>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  aria-invalid={Boolean(errors.password)}
                  {...register('password')}
                />
                <FieldError errors={[errors.password]} />
              </Field>
              <Button type="submit" className="w-full" disabled={signIn.isPending}>
                {signIn.isPending ? 'Signing in…' : 'Sign in'}
              </Button>
            </FieldGroup>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function loginErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    // Never say which of the two was wrong.
    if (error.isUnauthorized) return 'Email or password is incorrect.';
    return error.message;
  }
  return 'Sign-in failed. Try again.';
}

/** Only in-app paths are followed after sign-in, never another origin. */
function safeDestination(state: unknown): string {
  const from =
    typeof state === 'object' && state !== null ? (state as { from?: unknown }).from : undefined;
  if (
    typeof from !== 'string' ||
    !from.startsWith('/') ||
    from.startsWith('//') ||
    from.startsWith('/login')
  ) {
    return '/';
  }
  return from;
}
